import type { FeedLog } from '../../types/production'
// resolvePondLots moved to ./pondLotResolution so Mortality (and any future
// lot-scoped stream) can share the identical D-3 pond → lot resolution.
// Re-exported here so existing import sites keep working.
export { resolvePondLots, type ResolvedPondLots } from './pondLotResolution'

// Feed v0 PRD §11 AC #6: quantity must be a positive number. Unlike
// WaterLog's four parameters, FeedLogService.ts has no server-side maximum
// on feedKg (only `min: 0`) — there is no authoritative bound to mirror.
// PRD §10 risk #4 flags this as its own small decision: a soft,
// non-blocking "confirm this is right" warning (same warn-don't-block shape
// as the duplicate check below), not a hard validation error, since a
// legitimately large pond feeding is possible and we have no basis to
// reject it outright. This threshold is a Claude-proposed default per
// Charter U-2's "numbers proposed by Claude, reviewed by Control Tower"
// pattern — re-tune from real pilot data in Stage 5, not guessed twice.
export const LARGE_FEED_KG_WARNING_THRESHOLD = 200

export type FeedKgValidation =
  | { ok: true }
  | { ok: false; reason: 'invalid' }

export const validateFeedKgValue = (rawValue: string): FeedKgValidation => {
  const trimmed = rawValue.trim()
  if (trimmed.length === 0) return { ok: false, reason: 'invalid' }

  const value = Number(trimmed.replace(',', '.'))
  if (!Number.isFinite(value) || value <= 0) return { ok: false, reason: 'invalid' }

  return { ok: true }
}

export const isUnusuallyLargeFeedKg = (value: number): boolean => value > LARGE_FEED_KG_WARNING_THRESHOLD

// Feed v0 PRD §3/§10 P0 "Bloqueo de duplicados razonable": warn, never
// hard-block, on a same-lot entry within a short window. Same 15-minute
// default and warn-don't-block shape as WaterLog's precedent
// (waterCaptureValidation.ts), but scoped by `lotId` (Feed is lot-scoped,
// D-1) rather than by production unit + parameter.
export const DUPLICATE_WARNING_WINDOW_MS = 15 * 60 * 1000

export const findLikelyDuplicateFeedLog = (args: {
  lotId: string
  recordedAtIso: string
  existingLogs: FeedLog[]
}): FeedLog | null => {
  const candidateMs = Date.parse(args.recordedAtIso)
  if (!Number.isFinite(candidateMs)) return null

  for (const log of args.existingLogs) {
    if (log.lotId !== args.lotId) continue

    const existingMs = Date.parse(log.recordedAt)
    if (!Number.isFinite(existingMs)) continue

    if (Math.abs(existingMs - candidateMs) <= DUPLICATE_WARNING_WINDOW_MS) {
      return log
    }
  }

  return null
}

/**
 * Builds the create payload for a single Crew Mode Feed capture. Pure and
 * independently testable — mirrors `buildWaterCaptureRow`'s discipline:
 * always stamps `capturedByActorId` from the active operator session
 * (Charter D-4) rather than leaving it for the server's device-fallback,
 * and never includes any cost/derived-indicator field (O-1/O-3) — the item
 * selection and quantity are facts; nothing here interprets them.
 */
export type FeedCaptureCreatePayload = {
  farmId: string
  productionUnitId: string
  lotId: string
  // Findings #136: the lot's name, as Mortality/Water/Weight captures send it.
  lot?: string
  feedType: string
  feedKg: number
  recordedAt: string
  capturedByActorId: string | undefined
  metadata?: Record<string, unknown>
}

export const buildFeedCaptureCreatePayload = (args: {
  farmId: string
  productionUnitId: string
  lotId: string
  lot?: string
  feedType: string
  feedKgValue: number
  recordedAtIso: string
  capturedByActorId: string | undefined
  // Office Plans v0 §3.5: lot number of the feed's latest delivery.
  feedLotNumber?: string
  // Findings #182: extra metadata, e.g. the "Pesada del día" weights.
  metadata?: Record<string, unknown>
}): FeedCaptureCreatePayload => {
  const metadata = { ...(args.feedLotNumber ? { feedLotNumber: args.feedLotNumber } : {}), ...(args.metadata ?? {}) }
  return {
    farmId: args.farmId,
    productionUnitId: args.productionUnitId,
    lotId: args.lotId,
    ...(args.lot?.trim() ? { lot: args.lot.trim() } : {}),
    feedType: args.feedType,
    feedKg: args.feedKgValue,
    recordedAt: args.recordedAtIso,
    capturedByActorId: args.capturedByActorId,
    ...(Object.keys(metadata).length > 0 ? { metadata } : {})
  }
}

/**
 * Office Plans v0 §3.2 / AC #3: feeding X kg of a transition mix creates one
 * payload per component (kg split by `splitMixKg`, last component takes the
 * rounding remainder), all sharing `metadata.mixId` and carrying `mixName`
 * and `mixPercent`. Each component keeps its own feed type (so its own cost
 * and stock) and its own delivery lot number.
 */
export const buildMixFeedCapturePayloads = (args: {
  farmId: string
  productionUnitId: string
  lotId: string
  lot?: string
  mixId: string
  mixName: string
  components: Array<{ feedType: string; percent: number; kg: number; feedLotNumber?: string }>
  recordedAtIso: string
  capturedByActorId: string | undefined
}): FeedCaptureCreatePayload[] =>
  args.components
    .filter((component) => component.kg > 0)
    .map((component) => {
      const base = buildFeedCaptureCreatePayload({
        farmId: args.farmId,
        productionUnitId: args.productionUnitId,
        lotId: args.lotId,
        lot: args.lot,
        feedType: component.feedType,
        feedKgValue: component.kg,
        recordedAtIso: args.recordedAtIso,
        capturedByActorId: args.capturedByActorId
      })
      return {
        ...base,
        metadata: {
          mixId: args.mixId,
          mixName: args.mixName,
          mixPercent: component.percent,
          ...(component.feedLotNumber ? { feedLotNumber: component.feedLotNumber } : {})
        }
      }
    })
